import { ShieldCheck, Luggage, CreditCard, BookOpen, Flag, Compass, ExternalLink } from "lucide-react";
import { FadeUp, Overline } from "./Reveal";

const STEPS = [
  { num: "01", title: "Comprendre ma situation" },
  { num: "02", title: "Découvrir mon parcours" },
  { num: "03", title: "Me former" },
  { num: "04", title: "Préparer l'examen civique" },
  { num: "05", title: "Comprendre mes démarches" },
  { num: "06", title: "Progresser vers mon objectif" },
];

export function Parcours() {
  return (
    <section id="parcours" className="bg-slate-50/70 py-20 sm:py-28">
      <div className="container-integra">
        <FadeUp className="mx-auto max-w-2xl text-center">
          <Overline>Mon parcours</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Votre parcours, étape par étape.
          </h2>
        </FadeUp>

        <div className="relative mx-auto mt-14 max-w-3xl">
          <div className="absolute left-6 top-0 h-full w-px bg-gradient-to-b from-bleu via-bleu/30 to-transparent sm:left-1/2" />
          <div className="space-y-10">
            {STEPS.map((s, i) => (
              <FadeUp key={s.num} delay={i * 0.08}>
                <div className={`relative flex items-center gap-6 ${i % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"}`}>
                  <div className={`hidden flex-1 sm:block ${i % 2 === 0 ? "text-right" : "text-left"}`}>
                    <p className="font-display text-lg font-bold text-slate-900">{s.title}</p>
                  </div>
                  <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-4 border-slate-50 bg-bleu font-display text-sm font-extrabold text-white shadow-lg shadow-bleu/25">
                    {s.num}
                  </div>
                  <div className="flex-1 sm:hidden">
                    <p className="font-display text-lg font-bold text-slate-900">{s.title}</p>
                  </div>
                  <div className="hidden flex-1 sm:block" />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp delay={0.2}>
          <p className="mx-auto mt-14 max-w-2xl rounded-2xl border border-bleu/15 bg-white px-6 py-5 text-center font-display text-lg font-bold leading-relaxed text-bleu shadow-sm">
            « Vous savez où vous êtes, ce que vous avez déjà accompli et quelle est votre prochaine étape. »
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

const SOURCES = [
  { name: "Service-Public.fr", role: "Fiches pratiques et formalités administratives officielles", url: "https://www.service-public.fr" },
  { name: "Ministère de l'Intérieur", role: "Textes officiels sur la nationalité et le séjour", url: "https://www.interieur.gouv.fr" },
  { name: "ANEF", role: "Administration numérique pour les étrangers en France", url: "https://administration-etrangers-en-france.interieur.gouv.fr" },
  { name: "OFII", role: "Office français de l'immigration et de l'intégration", url: "https://www.ofii.fr" },
];

export function Sources() {
  return (
    <section id="ressources" className="py-20 sm:py-28">
      <div className="container-integra">
        <FadeUp className="max-w-2xl">
          <Overline>Des informations fiables</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            L'intelligence artificielle, <span className="text-bleu">avec des sources fiables.</span>
          </h2>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">
            INTEGRA FRANCE est conçu pour vous aider à comprendre les informations, pas pour les remplacer.
          </p>
        </FadeUp>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SOURCES.map((s, i) => (
            <FadeUp key={s.name} delay={i * 0.1} className="h-full">
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="card-integra card-hover group flex h-full flex-col p-6"
                data-testid={`source-card-${i}`}
              >
                <span className="inline-flex w-fit rounded-2xl bg-emerald-50 p-3">
                  <ShieldCheck className="h-6 w-6 text-emeraude" />
                </span>
                <h3 className="mt-4 flex items-center gap-1.5 font-display text-base font-bold text-slate-900">
                  {s.name}
                  <ExternalLink className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-bleu" />
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.role}</p>
              </a>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.15}>
          <p className="mt-8 rounded-2xl bg-bleu-light/60 px-6 py-5 text-sm font-medium leading-relaxed text-bleu">
            Lorsque votre question concerne une démarche administrative, INTEGRA FRANCE privilégie les sources
            officielles et vous permet de vérifier l'information.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

const PROFILES = [
  { icon: Luggage, title: "Je viens d'arriver en France", desc: "Je veux comprendre les premières étapes de mon installation.", color: "text-bleu bg-bleu-light" },
  { icon: CreditCard, title: "Je suis déjà en France", desc: "Je veux mieux comprendre mes démarches et mon parcours.", color: "text-violet-600 bg-violet-50" },
  { icon: BookOpen, title: "Je prépare mon examen civique", desc: "Je veux apprendre, réviser et m'entraîner.", color: "text-rouge bg-rose-50" },
  { icon: Flag, title: "Je prépare mon projet de naturalisation", desc: "Je veux comprendre les différentes étapes de mon parcours.", color: "text-emeraude bg-emerald-50" },
  { icon: Compass, title: "Je ne sais pas par où commencer", desc: "INTEGRA FRANCE m'aide à identifier ma prochaine étape.", color: "text-ambre bg-amber-50" },
];

export function Profiles() {
  return (
    <section id="profils" className="bg-slate-50/70 py-20 sm:py-28">
      <div className="container-integra">
        <FadeUp className="max-w-2xl">
          <Overline>Pour qui ?</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            INTEGRA FRANCE est conçu pour vous accompagner.
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {PROFILES.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.08} className="h-full">
              <div className="card-integra card-hover h-full p-6" data-testid={`profile-card-${i}`}>
                <span className={`inline-flex rounded-2xl p-3 ${p.color}`}>
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display text-[15px] font-bold leading-snug text-slate-900">{p.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-500">{p.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
